import { createGlobalStyle } from 'styled-components';
import { theme } from './theme';

export const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Poppins:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');
  
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  
  ::selection {
    background: ${theme.colors.primary};
    color: ${theme.colors.secondary};
  }
  
  html {
    font-size: 16px;
    scroll-behavior: smooth;
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      font-size: 14px;
    }
  }
  
  body {
    font-family: ${theme.fonts.main};
    background-color: ${theme.colors.background};
    color: ${theme.colors.text};
    line-height: 1.6;
    min-height: 100vh;
    overflow-x: hidden;
    letter-spacing: 0.01em;
  }
  
  h1, h2, h3, h4, h5, h6 {
    font-family: ${theme.fonts.heading};
    font-weight: 600;
    margin-bottom: ${theme.spacing.md};
    line-height: 1.3;
    letter-spacing: -0.02em;
  }
  
  h1 {
    font-size: 2.75rem;
    
    @media (min-width: ${theme.breakpoints.tablet}) {
      font-size: 3.5rem;
    }
    
    @media (min-width: ${theme.breakpoints.desktop}) {
      font-size: 4rem;
    }
  }
  
  h2 {
    font-size: 2.25rem;
    
    @media (min-width: ${theme.breakpoints.tablet}) {
      font-size: 2.5rem;
    }
  }
  
  h3 {
    font-size: 1.5rem;
    
    @media (min-width: ${theme.breakpoints.tablet}) {
      font-size: 1.75rem;
    }
  }
  
  h4 {
    font-size: 1.25rem;
  }
  
  h5 {
    font-size: 1.125rem;
  }
  
  h6 {
    font-size: 1rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  a {
    text-decoration: none;
    color: ${theme.colors.primary};
    transition: ${theme.transitions.short};
    position: relative;
    
    &:hover {
      color: ${theme.colors.primaryDark};
    }
  }
  
  button {
    cursor: pointer;
    font-family: ${theme.fonts.main};
    transition: ${theme.transitions.short};
    border: none;
    outline: none;
    background: none;
    
    &:focus {
      outline: none;
    }
    
    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
  
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }
  
  input, textarea, select {
    font-family: ${theme.fonts.main};
    font-size: 1rem;
    transition: ${theme.transitions.short};
    
    &:focus {
      outline: none;
    }
  }
  
  ul, ol {
    list-style-type: none;
    padding-left: 0;
  }
  
  .container {
    width: 100%;
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 ${theme.spacing.md};
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      padding: 0 ${theme.spacing.sm};
    }
    
    @media (min-width: ${theme.breakpoints.desktop}) {
      padding: 0 ${theme.spacing.lg};
    }
  }
  
  .section {
    padding: ${theme.spacing.xl} 0;
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      padding: ${theme.spacing.lg} 0;
    }
    
    @media (min-width: ${theme.breakpoints.desktop}) {
      padding: ${theme.spacing.xxl} 0;
    }
  }
  
  /* Utility Classes */
  .text-center {
    text-align: center;
  }
  
  .text-right {
    text-align: right;
  }
  
  .flex {
    display: flex;
  }
  
  .flex-center {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  .flex-between {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .flex-column {
    display: flex;
    flex-direction: column;
  }
  
  .mt-sm { margin-top: ${theme.spacing.sm}; }
  .mt-md { margin-top: ${theme.spacing.md}; }
  .mt-lg { margin-top: ${theme.spacing.lg}; }
  .mb-sm { margin-bottom: ${theme.spacing.sm}; }
  .mb-md { margin-bottom: ${theme.spacing.md}; }
  .mb-lg { margin-bottom: ${theme.spacing.lg}; }
  
  /* Animation classes */
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  
  @keyframes slideDown {
    from { transform: translateY(-20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
  
  .fade-in {
    animation: fadeIn 0.6s ease-in-out;
  }
  
  .slide-up {
    animation: slideUp 0.6s ease-in-out;
  }
  
  .slide-down {
    animation: slideDown 0.6s ease-in-out;
  }
  
  .pulse {
    animation: pulse 2s infinite;
  }
  
  /* Responsive utilities */
  .hide-on-mobile {
    @media (max-width: ${theme.breakpoints.mobile}) {
      display: none !important;
    }
  }
  
  .show-on-mobile {
    display: none !important;
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      display: block !important;
    }
  }
  
  .flex-on-mobile {
    @media (max-width: ${theme.breakpoints.mobile}) {
      display: flex !important;
    }
  }
  
  .flex-column-on-mobile {
    @media (max-width: ${theme.breakpoints.mobile}) {
      flex-direction: column !important;
    }
  }
  
  .full-width-on-mobile {
    @media (max-width: ${theme.breakpoints.mobile}) {
      width: 100% !important;
    }
  }
  
  .text-center-on-mobile {
    @media (max-width: ${theme.breakpoints.mobile}) {
      text-align: center !important;
    }
  }
  
  .no-padding-on-mobile {
    @media (max-width: ${theme.breakpoints.mobile}) {
      padding: 0 !important;
    }
  }
`; 